import Div from "@jumbo/shared/Div/Div";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {Button, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select, TextField} from "@mui/material";
import {useFormik} from "formik";
import React, {useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import * as Yup from "yup";
import {VALIDATION_MESSAGE} from "../../../constants/index";
import {getUniqueId} from "../../../utils/CommonMethods";

const InventorsForm = ({
                           removeFormFields,
                           formIndex,
                           setFormikObj,
                           formNumber,
                           contactsData,
                           setContactsData
                       }) => {

    const emptyContactFields = {
        contact_type: "",
        first_name: "",
        middle_name: "",
        last_name: "",
        name: "",
        nationality: "",
        organization_name: "",
        reference_no: "",
        role_type: "",
        attorney_role_type: "",
        home_address_line1: "",
        home_address_line2: "",
        home_city: "",
        home_state: "",
        home_country: "",
        home_postal_code: "",
        salutation: "",
        org_id: null,
        office_address_line1: "",
        office_address_line2: "",
        office_city: "",
        office_state: "",
        office_country: "",
        office_postal_code: "",
        office_email: "",
        office_telephone: "",
        party_type: "",
        sequence_no: ""
    };

    const [initialValues, setInitialValues] = useState(contactsData[formIndex] || emptyContactFields);
    const [roleOptions, setRoleOptions] = useState([]);
    const isFirstRender = useRef(true);

    const validateSequenceNumbers = (newContactsData, value) => {
        let partySequences = {};
        for (let key in newContactsData) {
            let partyType = newContactsData[key].party_type;
            let sequenceNumber = Number(newContactsData[key].sequence_no);

            if (!partySequences[partyType]) {
                partySequences[partyType] = [];
            }
            partySequences[partyType].push(sequenceNumber);
        }

        for (let partyType in partySequences) {

            if (partyType == formik?.values?.party_type) {
                let sequences = partySequences[partyType];

                const invalidSequence = value > sequences.length;

                if (invalidSequence) {
                    const error = "Sequence number cannot be greater than the total number of role entries";
                    return {status: false, error};
                }
                let hasDuplicates = new Set(sequences).size !== sequences.length;

                if (hasDuplicates) {
                    const error = "Duplicate sequence number";
                    return {status: false, error};
                }
            }
        }

        return true;
    };

    const validatePartySeqNo = (stateData, partyType, sequenceNo, formIndex) => {
        for (let key in stateData) {
            if (key !== formIndex && stateData[key]?.party_type === partyType && stateData[key]?.sequence_no > sequenceNo) {
                stateData[key].sequence_no -= 1;
                if (stateData[key].sequence_no === 1) {
                    stateData[key].role_type = "Primary";
                } else {
                    stateData[key].role_type = "Other";
                }
            }
        }
        return stateData;
    };

    // Validation schema defined
    const validationSchema = Yup.object({
        party_type: Yup.string().required(VALIDATION_MESSAGE.required),
        contact_type: Yup.string().required(VALIDATION_MESSAGE.required),
        role_type: Yup.string()
                      .when(["party_type"], {
                          is: (party_type) => party_type !== "",
                          then: Yup.string(VALIDATION_MESSAGE.required).test("validate-role_type", "invalid role type", function (value) {
                              const {path, createError} = this;
                              if (value === "Primary" && String(formik.values.sequence_no) !== "1") {
                                  return createError({path, message: "Primary isn't applicable for sequence no. greater than 1"});
                              } else if (value === "Other" && String(formik.values.sequence_no) === "1") {
                                  return createError({path, message: "Other isn't applicable for sequence no. 1"});
                              }
                              return true;
                          }),
                          otherwise: Yup.string().nullable()
                      }),
        sequence_no: Yup.number().nullable().typeError(VALIDATION_MESSAGE.number).test(
            "validate-sequence", // Unique test name
            "invalid sequence number", // Default error message
            function (value) {
                const {path, createError} = this;
                if (value < 1) {
                    return false;
                }
                // Pass the entered value as an argument to the validation function
                const validationResult = validateSequenceNumbers(contactsData, value);
                if (validationResult.status === false) {
                    return createError({path, message: validationResult.error});
                }
                return true;
            }
        ),
        first_name: Yup.string()
                       .when(["contact_type", "party_type"], {
                           is: (contact_type, party_type) => contact_type === "Individual" && party_type !== "Examiner",
                           then: Yup.string().required(VALIDATION_MESSAGE.required).trim("No leading/trailing spaces allowed"),
                           otherwise: Yup.string().nullable()
                       }),
        last_name: Yup.string()
                      .when(["contact_type", "party_type"], {
                          is: (contact_type, party_type) => contact_type === "Individual" && party_type !== "Examiner",
                          then: Yup.string().required(VALIDATION_MESSAGE.required).trim("No leading/trailing spaces allowed"),
                          otherwise: Yup.string().nullable()
                      }),
        name: Yup.string()
                 .when(["contact_type", "party_type"], {
                     is: (contact_type, party_type) => contact_type === "Organization" || party_type === "Examiner",
                     then: Yup.string().required(VALIDATION_MESSAGE.required).trim("No leading/trailing spaces allowed")
                 }),
        org_id: Yup.number().nullable().typeError(VALIDATION_MESSAGE.number)
    });

    const formik = useFormik({
        initialValues: initialValues,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: () => {
        }
    });

    useEffect(() => {
        formik.validateForm();
    }, [contactsData]);

    useEffect(() => {
        if (formik.values.party_type !== "") {
            if (String(formik.values.sequence_no) === "0") {
                formik.setFieldValue("role_type", "");
            } else if (String(formik.values.sequence_no) === "1") {
                formik.setFieldValue("role_type", "Primary");
            } else if (formik.values.sequence_no > 1) {
                formik.setFieldValue("role_type", "Other");
            }
        }

        if (formik.values.party_type === "") {
            formik.setFieldValue("sequence_no", "");
        }
        // setContactsData(prevState => ({...prevState, [formIndex]: formik.values}));
        // setFormikObj((prevState) => {
        //     return {...prevState, [formIndex]: formik};
        // });
    }, [formik.values.sequence_no]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        let roleCount = {
            Attorney: 0,
            Inventor: 0,
            Applicant: 0,
            Assignee: 0,
            Examiner: 0,
            Client: 0,
            "Legal Assistant": 0
        };

        let newContactsData = {...contactsData};
        if (newContactsData[formIndex]?.sequence_no !== "") {
            newContactsData = validatePartySeqNo(newContactsData, newContactsData[formIndex].party_type, newContactsData[formIndex].sequence_no, formIndex);
        }

        let partySequences = [];
        for (let key in newContactsData) {
            let partyType = newContactsData[key].party_type;
            if (partyType === "" && key === formIndex) {
                partyType = formik.values.party_type;
            }
            let sequenceNumber = Number(newContactsData[key].sequence_no);

            if (!partySequences[partyType]) {
                partySequences[partyType] = [];
            }
            partySequences[partyType].push(sequenceNumber);
        }

        Object.keys(newContactsData)?.forEach((key) => {
            let partyType = newContactsData[key]?.party_type;
            if (partyType === "" && key === formIndex) {
                partyType = formik.values.party_type;
            }

            if (!roleCount[partyType]) {
                roleCount[partyType] = 0;
            }
            key !== formIndex && roleCount[partyType]++;
            const fromSet = new Set(partySequences[formik.values.party_type]);
            let seqNo = 1;
            while (fromSet.has(seqNo)) {
                seqNo++;
            }
            if (partyType !== formik.values.party_type && key === formIndex) {
                newContactsData[key].sequence_no = seqNo;
                newContactsData[key].party_type = formik.values.party_type;
            } else if (partyType === formik.values.party_type && partyType !== "") {
                if (key === formIndex && !contactsData[formIndex].sequence_no) {
                    newContactsData[key].sequence_no = seqNo;
                    newContactsData[key].party_type = formik.values.party_type;
                }
            }

        });

        formik.setFieldValue("sequence_no", newContactsData?.[formIndex]?.sequence_no);

        if (formik.values.party_type === "Examiner") {
            formik.setFieldValue("first_name", "");
            formik.setFieldValue("middle_name", "");
            formik.setFieldValue("last_name", "");
        }

        if (formik.values.party_type === "") {
            formik.setFieldValue("sequence_no", "");
        }
        setContactsData(() => {
            return {...newContactsData, [formIndex]: formik.values};
        });
        setFormikObj(() => {
            return {...newContactsData, [formIndex]: formik};
        });
    }, [formik.values.party_type]);

    useEffect(() => {
        // Update the sequence number based on the selected role
        const selectedContactType = formik.values.contact_type;
        if (selectedContactType === "Organization") {
            // Show specific "Role" options for "Individual"
            if (contactsData?.[formIndex]?.contact_type !== selectedContactType) {
                formik.setFieldValue("party_type", "");
                formik.setFieldValue("role_type", "");
                formik.setFieldValue("sequence_no", "");
            }

            setRoleOptions(["Applicant", "Assignee", "Client", "Legal Entity"]);
            formik.setFieldValue("first_name", "");
            formik.setFieldValue("middle_name", "");
            formik.setFieldValue("last_name", "");
        } else {
            // Show all "Role" options for other types
            if (selectedContactType !== contactsData?.[formIndex]?.contact_type) {
                formik.setFieldValue("party_type", "");
                formik.setFieldValue("role_type", "");
                formik.setFieldValue("sequence_no", "");
            }

            setRoleOptions([
                "Attorney",
                "Inventor",
                "Examiner",
                "Legal Assistant"
            ]);
        }
    }, [formik.values.contact_type]);

    useEffect(() => {
        // To set the full name of the user
        let firstName = formik.values.first_name?.trim(" ") || "";
        let midName = formik.values.middle_name?.trim(" ") || "";
        let lastName = formik.values.last_name?.trim(" ") || "";


        let fullName = "";
        if (firstName !== "" || midName !== "" || lastName !== "") {
            if (firstName !== "") {
                fullName += firstName + " ";
            }
            if (midName !== "") {
                fullName += midName + " ";
            }
            if (lastName !== "") {
                fullName += lastName + " ";
            }
        } else {
            fullName = firstName + " " + midName + " " + lastName;
        }

        fullName = fullName.trim();
        if (formik.values.contact_type !== "Organization" && formik.values.party_type !== "Examiner" && formik.values.name !== fullName) {
            formik.setFieldValue("name", fullName);
        }
    }, [formik.values.first_name, formik.values.middle_name, formik.values.last_name, formik.values.contact_type, formik.values.party_type]);

    useEffect(() => {
        // Propagate the formik value up to the shared contactsData/formikObj state.
        setContactsData((prevState) => {
            return {...prevState, [formIndex]: formik.values};
        });
        setFormikObj((prevState) => {
            return {...prevState, [formIndex]: formik};
        });
    }, [formik.values]);


    //#endregion
    return (
        <Paper sx={{py: "12px", mb: "20px"}}>
            <header className="sub-form-header">
                <div className="heading-area">
                    <h5>CONTACT - {formNumber + 1}</h5>
                </div>
                <div className="action-area">
                    <Button
                        type="button"
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => removeFormFields(formIndex)}
                        startIcon={<RemoveCircleOutlineIcon/>}
                    >
                        Remove
                    </Button>
                </div>
            </header>
            <form onSubmit={formik.handleSubmit}>
                <div className="container">
                    <h6 className="form-sub-heading">Details</h6>
                    <div className="row">
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <FormControl className="w-100" size="small">
                                    <InputLabel id="contact_type-label">Contact Type *</InputLabel>
                                    <Select
                                        labelId="contact_type-label"
                                        id="contact_type"
                                        name="contact_type"
                                        label="Contact Type"
                                        value={formik.values.contact_type}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            // Update "Role" options based on "Contact Type"
                                            const selectedContactType = e.target.value;
                                            if (selectedContactType === "Organization") {
                                                // Show specific "Role" options for "Individual"
                                                setRoleOptions(["Applicant", "Assignee", "Client", "Legal Entity"]);
                                            } else {
                                                // Show all "Role" options for other types
                                                setRoleOptions([
                                                    "Attorney",
                                                    "Inventor",
                                                    "Examiner",
                                                    "Legal Assistant"
                                                ]);
                                            }
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.contact_type &&
                                            Boolean(formik.errors.contact_type)
                                        }
                                        helperText={
                                            formik.touched.contact_type && formik.errors.contact_type
                                        }
                                    >
                                        <MenuItem value={"Individual"}>Individual</MenuItem>
                                        <MenuItem value={"Organization"}>Organization</MenuItem>
                                    </Select>
                                    {formik.touched.contact_type &&
                                        formik.errors.contact_type && (
                                            <div style={{color: "red"}}>
                                                {formik.errors.contact_type}
                                            </div>
                                        )}
                                </FormControl>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <FormControl className="w-100" size="small">
                                    <InputLabel id="party_type-label">Role *</InputLabel>
                                    <Select
                                        labelId="party_type-label"
                                        id="party_type"
                                        name="party_type"
                                        label="Role"
                                        value={formik.values.party_type}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.party_type &&
                                            Boolean(formik.errors.party_type)
                                        }
                                        helperText={
                                            formik.touched.party_type && formik.errors.party_type
                                        }
                                    >
                                        {roleOptions?.map((roleOption) => (
                                            <MenuItem key={roleOption} value={roleOption}>
                                                {roleOption}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.party_type && formik.errors.party_type && (
                                        <div style={{color: "red"}}>
                                            {formik.errors.party_type}
                                        </div>
                                    )}
                                </FormControl>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="salutation"
                                    name="salutation"
                                    label="Salutation"
                                    value={formik.values.salutation}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.salutation &&
                                        Boolean(formik.errors.salutation)
                                    }
                                    helperText={
                                        formik.touched.salutation && formik.errors.salutation
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <FormControl className="w-100" size="small">
                                    <InputLabel id="role_type-label">Role Type *</InputLabel>
                                    <Select
                                        labelId="role_type-label"
                                        id="role_type"
                                        name="role_type"
                                        label="Role Type"
                                        value={formik.values.role_type}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.role_type &&
                                            Boolean(formik.errors.role_type)
                                        }
                                        helperText={
                                            formik.touched.role_type && formik.errors.role_type
                                        }
                                    >
                                        <MenuItem value={"Primary"}>Primary</MenuItem>
                                        <MenuItem value={"Other"}>Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                        </div>

                        {formik.values.party_type === "Attorney" && <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="attorney_role_type"
                                    name="attorney_role_type"
                                    label="Attorney - Role Type"
                                    value={formik.values.attorney_role_type}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.attorney_role_type && Boolean(formik.errors.attorney_role_type)}
                                    helperText={formik.touched.attorney_role_type && formik.errors.attorney_role_type}
                                />
                            </div>
                        </div>}
                    </div>

                    <div className="row">
                        {(formik.values.contact_type !== "Organization" && formik.values.party_type !== "Examiner") && <>
                            <div className="col-lg-4 col-md-6">
                                <div className="form-group">
                                    <TextField
                                        type="text"
                                        fullWidth
                                        size="small"
                                        id="first_name"
                                        name="first_name"
                                        label="First Name *"
                                        value={formik.values.first_name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.first_name &&
                                            Boolean(formik.errors.first_name)
                                        }
                                        helperText={
                                            formik.touched.first_name && formik.errors.first_name
                                        }
                                    />
                                </div>
                            </div>
                            <div className="col-lg-4 col-md-6">
                                <div className="form-group">
                                    <TextField
                                        type="text"
                                        fullWidth
                                        size="small"
                                        id="middle_name"
                                        name="middle_name"
                                        label="Middle Name"
                                        value={formik.values.middle_name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.middle_name &&
                                            Boolean(formik.errors.middle_name)
                                        }
                                        helperText={
                                            formik.touched.middle_name && formik.errors.middle_name
                                        }
                                    />
                                </div>
                            </div>
                            <div className="col-lg-4 col-md-6">
                                <div className="form-group">
                                    <TextField
                                        type="text"
                                        fullWidth
                                        size="small"
                                        id="last_name"
                                        name="last_name"
                                        label="Last Name *"
                                        value={formik.values.last_name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.last_name && Boolean(formik.errors.last_name)
                                        }
                                        helperText={
                                            formik.touched.last_name && formik.errors.last_name
                                        }
                                    />
                                </div>
                            </div>
                        </>}
                        <div className="col-12">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="name"
                                    name="name"
                                    label="Name *"
                                    value={formik.values.name}
                                    InputProps={{
                                        readOnly: formik.values.contact_type === "Individual" && formik.values.party_type !== "Examiner"
                                    }}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="nationality"
                                    name="nationality"
                                    label="Nationality"
                                    value={formik.values.nationality}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.nationality &&
                                        Boolean(formik.errors.nationality)
                                    }
                                    helperText={
                                        formik.touched.nationality && formik.errors.nationality
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="reference_no"
                                    name="reference_no"
                                    label="Reference No."
                                    value={formik.values.reference_no}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.reference_no &&
                                        Boolean(formik.errors.reference_no)
                                    }
                                    helperText={
                                        formik.touched.reference_no && formik.errors.reference_no
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="sequence_no"
                                    name="sequence_no"
                                    label="Sequence No."
                                    value={formik.values.sequence_no}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.sequence_no &&
                                        Boolean(formik.errors.sequence_no)
                                    }
                                    helperText={
                                        formik.touched.sequence_no && formik.errors.sequence_no
                                    }
                                />
                            </div>
                        </div>
                    </div>
                    <h6 className="form-sub-heading">Office</h6>
                    <div className="row">
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="organization_name"
                                    name="organization_name"
                                    label="Organization Name"
                                    value={formik.values.organization_name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.organization_name &&
                                        Boolean(formik.errors.organization_name)
                                    }
                                    helperText={
                                        formik.touched.organization_name &&
                                        formik.errors.organization_name
                                    }
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row mb-3">
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="office_address_line1"
                                    name="office_address_line1"
                                    label="Office Address Line 1"
                                    value={formik.values.office_address_line1}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.office_address_line1 &&
                                        Boolean(formik.errors.office_address_line1)
                                    }
                                    helperText={
                                        formik.touched.office_address_line1 &&
                                        formik.errors.office_address_line1
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="office_address_line2"
                                    name="office_address_line2"
                                    label="Office Address Line 2"
                                    value={formik.values.office_address_line2}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.office_address_line2 &&
                                        Boolean(formik.errors.office_address_line2)
                                    }
                                    helperText={
                                        formik.touched.office_address_line2 &&
                                        formik.errors.office_address_line2
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="office_city"
                                    name="office_city"
                                    label="Office City"
                                    value={formik.values.office_city}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.office_city &&
                                        Boolean(formik.errors.office_city)
                                    }
                                    helperText={
                                        formik.touched.office_city && formik.errors.office_city
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="office_state"
                                    name="office_state"
                                    label="Office State"
                                    value={formik.values.office_state}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.office_state &&
                                        Boolean(formik.errors.office_state)
                                    }
                                    helperText={
                                        formik.touched.office_state && formik.errors.office_state
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="office_postal_code"
                                    name="office_postal_code"
                                    label="Office Postal Code"
                                    value={formik.values.office_postal_code}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.office_postal_code &&
                                        Boolean(formik.errors.office_postal_code)
                                    }
                                    helperText={
                                        formik.touched.office_postal_code &&
                                        formik.errors.office_postal_code
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="office_country"
                                    name="office_country"
                                    label="Office Country"
                                    value={formik.values.office_country}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.office_country &&
                                        Boolean(formik.errors.office_country)
                                    }
                                    helperText={
                                        formik.touched.office_country &&
                                        formik.errors.office_country
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="office_email"
                                    name="office_email"
                                    label="Office Email"
                                    value={formik.values.office_email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.office_email &&
                                        Boolean(formik.errors.office_email)
                                    }
                                    helperText={
                                        formik.touched.office_email && formik.errors.office_email
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="office_telephone"
                                    name="office_telephone"
                                    label="Office Telephone"
                                    value={formik.values.office_telephone}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.office_telephone &&
                                        Boolean(formik.errors.office_telephone)
                                    }
                                    helperText={
                                        formik.touched.office_telephone &&
                                        formik.errors.office_telephone
                                    }
                                />
                            </div>
                        </div>
                    </div>
                    <h6 className="form-sub-heading">Residence</h6>
                    <div className="row mb-3">
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="home_address_line1"
                                    name="home_address_line1"
                                    label="Home Address Line 1"
                                    value={formik.values.home_address_line1}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.home_address_line1 &&
                                        Boolean(formik.errors.home_address_line1)
                                    }
                                    helperText={
                                        formik.touched.home_address_line1 &&
                                        formik.errors.home_address_line1
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="home_address_line2"
                                    name="home_address_line2"
                                    label="Home Address Line 2"
                                    value={formik.values.home_address_line2}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.home_address_line2 &&
                                        Boolean(formik.errors.home_address_line2)
                                    }
                                    helperText={
                                        formik.touched.home_address_line2 &&
                                        formik.errors.home_address_line2
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="home_city"
                                    name="home_city"
                                    label="Home City"
                                    value={formik.values.home_city}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.home_city && Boolean(formik.errors.home_city)
                                    }
                                    helperText={
                                        formik.touched.home_city && formik.errors.home_city
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="home_state"
                                    name="home_state"
                                    label="Home State"
                                    value={formik.values.home_state}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.home_state &&
                                        Boolean(formik.errors.home_state)
                                    }
                                    helperText={
                                        formik.touched.home_state && formik.errors.home_state
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="home_postal_code"
                                    name="home_postal_code"
                                    label="Home Postal Code"
                                    value={formik.values.home_postal_code}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.home_postal_code &&
                                        Boolean(formik.errors.home_postal_code)
                                    }
                                    helperText={
                                        formik.touched.home_postal_code &&
                                        formik.errors.home_postal_code
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                            <div className="form-group">
                                <TextField
                                    type="text"
                                    fullWidth
                                    size="small"
                                    id="home_country"
                                    name="home_country"
                                    label="Home Country"
                                    value={formik.values.home_country}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.home_country &&
                                        Boolean(formik.errors.home_country)
                                    }
                                    helperText={
                                        formik.touched.home_country && formik.errors.home_country
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </Paper>
    );
};

const MatterContacts = ({
                            setActiveTab,
                            contactsData,
                            setContactsData,
                            formikObj,
                            setFormikObj,
                            handleClose,
                            matterId,
                            combinedMatterData,
                            setCombinedMatterData
                        }) => {

    const [loading, setLoading] = useState(false);
    const isMountedRef = useRef(true);
    useEffect(() => () => {
        isMountedRef.current = false;
    }, []);

    const addFormFields = () => {
        // Adding a new form with a unique ID
        const id = getUniqueId();
        setFormikObj((prevState) => {
            return {...prevState, [id]: {}};
        });
    };


    // Function to remove the form fields
    const removeFormFields = (formIndex) => {
        setFormikObj((prevState) => {
            const removedPartyType = prevState[formIndex]?.values?.party_type;
            const removedSequenceNo = prevState[formIndex]?.values?.sequence_no;

            const newState = {...prevState};
            delete newState[formIndex];

            for (let key in newState) {
                if (newState[key]?.values?.party_type === removedPartyType && newState[key]?.values?.sequence_no > removedSequenceNo) {
                    newState[key].values.sequence_no -= 1;
                    if (newState[key].values.sequence_no === 1) {
                        newState[key].values.role_type = "Primary";
                    } else {
                        newState[key].values.role_type = "Other";
                    }
                }
            }

            return newState;
        });

        const removedPartyType = contactsData[formIndex]?.party_type;
        const removedSequenceNo = contactsData[formIndex]?.sequence_no;

        const newState = {...contactsData};
        delete newState[formIndex];
        for (let key in newState) {
            if (newState[key]?.party_type === removedPartyType && newState[key]?.sequence_no > removedSequenceNo) {
                newState[key].sequence_no -= 1;
                if (newState[key].sequence_no === 1) {
                    newState[key].role_type = "Primary";
                } else {
                    newState[key].role_type = "Other";
                }
            }
        }
        setContactsData(newState);
    };


    const validateSequenceNumbers = (newContactsData) => {
        let partySequences = {};
        for (let key in newContactsData) {
            let partyType = newContactsData[key].party_type;
            let sequenceNumber = Number(newContactsData[key].sequence_no);

            if (!partySequences[partyType]) {
                partySequences[partyType] = [];
            }

            if (!sequenceNumber) {
                return false;
            }
            partySequences[partyType].push(sequenceNumber);
        }

        for (let partyType in partySequences) {
            let sequences = partySequences[partyType];
            const invalidSequence = sequences.some((item) => item > sequences.length);

            if (invalidSequence) {
                return false;
            }
            let hasDuplicates = new Set(sequences).size !== sequences.length;

            if (hasDuplicates) {
                return false;
            }
        }

        return true;
    };

    // Function for handling final submission of the form
    const finalSubmitHandler = async () => {
        try {
            let isValid = true;

            // Checking the validation for all the sub forms
            const validationPromises = Object.keys(formikObj).map(async (item) => {
                if (typeof formikObj[item]?.validateForm === "function") {
                    const validationObj = await formikObj[item]?.validateForm();

                    if (Object.keys(validationObj).length > 0) {
                        await formikObj[item].submitForm();
                        isValid = false;
                    }
                }
                return;
            });

            await Promise.all(validationPromises);

            let newContactsData = {...contactsData};

            if (isValid) isValid = validateSequenceNumbers(newContactsData);

            // if isValid is equal to true, then let the form submit
            if (isValid) {
                setContactsData(() => {

                    const contactsArray = Object.entries(newContactsData);


                    contactsArray.sort((a, b) => {
                        if (a[1].party_type === b[1].party_type) {
                            return a[1].sequence_no - b[1].sequence_no;
                        }
                        return a[1].party_type.localeCompare(b[1].party_type);
                    });

                    newContactsData = Object.fromEntries(contactsArray);

                    return newContactsData;
                });

                const transformedData = Object.keys(newContactsData).map((key) => {
                    const contact = contactsData[key];
                    return {
                        party_type: contact.party_type,
                        sequence_no: contact.sequence_no,
                        party: {
                            ...contact
                        }
                    };
                });
                setLoading(true);
                setCombinedMatterData((prevState) => ({...prevState, matter_parties: transformedData}));
                toast.success("Matter contacts saved successfully");
                setActiveTab(2);
            } else {
                Object.keys(formikObj).map((item) => {
                    formikObj[item].submitForm();
                });
                toast.error("Contact details aren't valid");
            }
        } catch (error) {
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };

    //#endregion

    return (
        <>
            <Div>
                <div className="form-action-area container mb-0 px-0">

                    {loading ? <CircularProgress/> : <Button
                        type="button"
                        variant="contained"
                        className="btn-theme"
                        onClick={() => finalSubmitHandler(true)}
                    >
                        Save & Next
                    </Button>}
                </div>

                {Object.keys(formikObj).map((objKey, index) => {
                    return (
                        <InventorsForm
                            key={objKey}
                            removeFormFields={removeFormFields}
                            formIndex={objKey}
                            setFormikObj={setFormikObj}
                            formNumber={index}
                            contactsData={contactsData}
                            setContactsData={setContactsData}
                        />
                    );
                })}
                <div className="sub-form-action-area mt-4">
                    <Button
                        type="button"
                        variant="contained"
                        className="btn-primary"
                        onClick={() => {
                            addFormFields();
                        }}
                        sx={{
                            width: "100%",
                            maxWidth: "300px",
                            m: "auto"
                        }}
                        startIcon={<AddIcon/>}
                    >
                        Add more
                    </Button>
                </div>
            </Div>
        </>
    );
};

export default MatterContacts;